import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { Button } from '@televet/kibble-ui/Button';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { trpc } from '@/lib/trpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { toaster } from '@/components/ui/toaster';
import {
  EXECUTION_MODE_CONFIG,
  formatDuration,
  namespacePalette,
} from '@/routes/_authenticated/emitters/-lib/executionConstants';
import { EXECUTION_MODE_TAG_VARIANT } from '@/routes/_authenticated/emitters/executions/-lib/records';
import { ExecutionStatusTag } from '@/routes/_authenticated/emitters/-components/StatusTag';

export const Route = createFileRoute(
  '/_authenticated/emitter-templates/$templateId/instances/$instanceId/executions'
)({
  component: InstanceExecutions,
});

const PAGE_SIZE = 50;

function InstanceExecutions() {
  const { templateId, instanceId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: template } = useQuery(trpc.emitter.getTemplate.queryOptions({ id: templateId }));

  const { data: instance } = useQuery(trpc.emitter.getInstance.queryOptions({ id: instanceId }));

  // Cursor-based pagination
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack[cursorStack.length - 1] as string | undefined;

  const { data, isLoading, isError, error } = useQuery(
    trpc.emitter.listExecutions.queryOptions({
      emitterId: [instanceId],
      limit: PAGE_SIZE,
      cursor: currentCursor,
    })
  );

  const pageNumber = cursorStack.length + 1;

  const goNextPage = useCallback(() => {
    if (data?.nextCursor) {
      setCursorStack((prev) => [...prev, data.nextCursor!]);
    }
  }, [data?.nextCursor]);

  const goPrevPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  const { mutate: processExecution, isPending: isProcessing } = useMutation(
    trpc.emitter.processExecution.mutationOptions({
      onSuccess: (result) => {
        if (result.status === 'Completed') {
          toaster.success({
            title: `Completed: ${result.totalResults} results${result.durationMs != null ? ` in ${formatDuration(result.durationMs)}` : ''}`,
          });
        } else {
          toaster.error({
            title: 'Execution failed',
            description: result.errorMessage ?? 'Unknown error',
          });
        }
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listExecutions']],
        });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to process',
          description: error.message,
        });
      },
    })
  );

  const { mutate: retryExecution, isPending: isRetrying } = useMutation(
    trpc.emitter.retryExecution.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Retry queued' });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listExecutions']],
        });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to retry',
          description: error.message,
        });
      },
    })
  );

  const templateName = template?.name ?? 'Template';
  const instanceName = instance?.name ?? instance?.id?.slice(0, 8) ?? 'Instance';

  return (
    <PageContainer>
      <Flex direction="column" gap={3} width="100%">
        <Breadcrumbs
          items={[
            { label: 'Templates', to: '/emitter-templates' },
            { label: templateName, to: `/emitter-templates/${templateId}` },
            {
              label: 'Instances',
              to: `/emitter-templates/${templateId}/instances`,
            },
            { label: instanceName },
            { label: 'Executions' },
          ]}
        />

        <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
          Executions -- {instanceName}
        </Text>

        {/* Loading state */}
        {isLoading && (
          <Flex justify="center" py={8}>
            <Text fontSize="sm" variant="subtle">
              Loading executions...
            </Text>
          </Flex>
        )}

        {/* Error state */}
        {isError && (
          <Flex justify="center" py={8}>
            <Text fontSize="sm" variant="danger">
              Failed to load executions: {error?.message ?? 'Unknown error'}
            </Text>
          </Flex>
        )}

        {!isLoading && !isError && (
          <>
            <Table.Root size="sm" width="100%" variant="outline">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader textAlign="center">Ver</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Mode</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Status</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Namespace</Table.ColumnHeader>
                  <Table.ColumnHeader>Results</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">Duration</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">Scheduled</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data?.items.map((execution) => {
                  const modeConfig =
                    EXECUTION_MODE_CONFIG[execution.executionMode] ?? EXECUTION_MODE_CONFIG.DryRun;

                  return (
                    <Table.Row
                      key={execution.id}
                      cursor="pointer"
                      _hover={{ bg: 'bg.muted' }}
                      onClick={() =>
                        navigate({
                          to: `/emitters/executions/${execution.id}`,
                        })
                      }
                    >
                      <Table.Cell textAlign="center">
                        <Text fontSize="xs" variant="subtle">
                          v{execution.templateVersion.versionNumber}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Tag
                          label={modeConfig.label}
                          variant={EXECUTION_MODE_TAG_VARIANT[execution.executionMode] ?? 'success'}
                          size="sm"
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <ExecutionStatusTag status={execution.status} />
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {execution.dataSourceNamespace ? (
                          <Tag
                            label={execution.dataSourceNamespace}
                            variant={namespacePalette(execution.dataSourceNamespace)}
                            size="sm"
                          />
                        ) : (
                          <Text fontSize="xs" variant="placeholder">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {execution.totalResults != null ? (
                          <Text fontSize="xs" variant="subtle">
                            {execution.newResults ?? 0} new / {execution.totalResults}
                            {execution.dedupedResults ? ` (${execution.dedupedResults} dup)` : ''}
                          </Text>
                        ) : (
                          <Text fontSize="xs" variant="placeholder">
                            -
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Text fontSize="xs" variant="subtle" fontFamily="mono">
                          {execution.durationMs != null
                            ? formatDuration(execution.durationMs)
                            : '-'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Text fontSize="xs" variant="subtle">
                          {execution.scheduledAt
                            ? new Date(execution.scheduledAt).toLocaleString()
                            : '-'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Flex gap={0.5} justify="flex-end" onClick={(e) => e.stopPropagation()}>
                          {execution.status === 'Pending' && (
                            <Button
                              size="xs"
                              variant="ghost"
                              iconName="play"
                              onClick={() => processExecution({ id: execution.id })}
                              disabled={isProcessing}
                              aria-label="Process"
                              title="Process now"
                            />
                          )}
                          {execution.status === 'Failed' && (
                            <Button
                              size="xs"
                              variant="ghost"
                              iconName="arrowCircle"
                              onClick={() => retryExecution({ id: execution.id })}
                              disabled={isRetrying}
                              aria-label="Retry"
                              title="Retry"
                            />
                          )}
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>

            {/* Pagination */}
            <Flex justify="space-between" align="center">
              <Text fontSize="xs" variant="subtle">
                {data?.items.length ?? 0} executions on this page
              </Text>
              <Flex align="center" gap={2}>
                <Text fontSize="sm" variant="subtle">
                  Page {pageNumber}
                </Text>
                <Button
                  size="xs"
                  variant="ghostNeutral"
                  iconName="chevronLeft"
                  onClick={goPrevPage}
                  disabled={cursorStack.length === 0}
                  aria-label="Previous page"
                />
                <Button
                  size="xs"
                  variant="ghostNeutral"
                  iconName="chevronRight"
                  onClick={goNextPage}
                  disabled={!data?.nextCursor}
                  aria-label="Next page"
                />
              </Flex>
            </Flex>

            {data?.items.length === 0 && (
              <Flex justify="center" py={6}>
                <Text fontSize="sm" variant="subtle">
                  No executions for this instance
                </Text>
              </Flex>
            )}
          </>
        )}
      </Flex>
    </PageContainer>
  );
}
